import { SingletonRouter, useRouter, withRouter } from 'next/router'
import React, { FC, useEffect, useState } from 'react'

import { compose } from '@reduxjs/toolkit'
import { diff } from 'deep-object-diff'
import { NextSeo } from 'next-seo'
import { connect } from 'react-redux'
import { InjectedFormProps, reduxForm } from 'redux-form'

import { Box, Container, Grid } from '@material-ui/core'
import { Pagination } from '@material-ui/lab'

import AppLayout from '~/components/layouts/AppLayout'
import AdsSlot from '~/components/shared/AdSlot'
import Breadcrumbs from '~/components/shared/Breadcrumbs'
import Flexbox from '~/components/shared/Flexbox'
import LoaderBox from '~/components/shared/LoaderBox'
import StickyContainer from '~/components/shared/StickyContainer'
import { Title } from '~/components/shared/Text'
import Desktop from '~/containers/Desktop'
import withMobile from '~/containers/withMobile'
import SearchFilters from '~/views/Search/Filters'
import SearchSidebar from '~/views/Search/SearchSidebar'
import SearchToolBar from '~/views/Search/SearchToolBar'

import { useMobile } from '~/hooks/responsive'
import useSearchHandler from '~/hooks/useSearchHandler'
import useSearchMeta from '~/hooks/useSearchMeta'
import DFPSlotsProvider from '~/utils/DFPSlotsProvider'
import { isOfType } from '~/utils/functions'
import Paths from '~/utils/paths'

import { IS_BOAT } from '~/brands'
import { SearchFormValues, SearchDTO } from '~/models/Search'

import SearchCatDescription from './SearchCatDescription'
import SearchProductList from './SearchProductList'

const item = IS_BOAT ? 'Boats' : 'Caravans'

const breadcrumbs = [{ href: '/', child: `${item} for Sale` }]

// const placeholderLinstings = Array.from(Array(12))

interface SearchProps {
  initialParams: SearchDTO
}

const SearchView: FC<InjectedFormProps & SingletonRouter & SearchProps> = ({
  initialParams,
  initialize,
  change
}) => {
  const [orientation, setOrientation] = useState<'list' | 'grid'>('list')
  const router = useRouter()

  const isMobile = useMobile()

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [])

  const {
    handleChangePage,
    listings,
    pagy,
    meta: metaSearch,
    isFetching,
    searchParams = { params: initialParams }
  } = useSearchHandler({
    change,
    initialize,
    path: router.asPath
  })

  const currentListings = listings

  const { params } = searchParams

  const adOptions = {
    pageType: 'results',
    adUnit: 'search',
    pageKey: 'results',
    targetingArguments: {
      cat: params.by_type,
      subcat: params.by_category?.join('|'),
      adtype: params.by_condition,
      make: params.by_make,
      model: params.by_model,
      location: params.by_location,
      price:
        params.price_min || params.price_max
          ? `${params.price_min || 'min'} - ${params.price_max || 'max'}`
          : undefined,
      odometer:
        params.odometer_min || params.odometer_max
          ? `${params.odometer_min || 'min'} - ${params.odometer_max || 'max'}`
          : undefined,
      year:
        params.min_year || params.max_year
          ? `${params.min_year || 'min'} - ${params.max_year || 'max'}`
          : undefined
    }
  }

  const {
    metaTitle,
    metaDescription,
    title,
    chosenCatDesc,
    chosenSubCatDesc
  } = useSearchMeta({
    searchParams: searchParams.params,
    item,
    metaSearch,
    pagyCount: pagy?.count
  })

  return (
    <>
      <NextSeo
        title={metaTitle}
        titleTemplate="%s"
        description={metaDescription}
        canonical={Paths.search.index}
        openGraph={{
          title: metaTitle,
          description: metaDescription
        }}
      />
      <DFPSlotsProvider {...adOptions}>
        <AdsSlot
          renderOutOfThePage
          slotId="gam_oop_pos0"
          shouldRefresh={() => false}
          sizes={[[1, 1]]}
          targetingArguments={{ pos: '0' }}
          disableSizeMaping
        />
        <AppLayout bgcolor="bg.primary.main">
          <Container maxWidth="xl">
            <Breadcrumbs items={breadcrumbs} />
            <Title my={2} variant="h2" as="h1" mb={4}>
              {title}
            </Title>
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4} lg={3}>
                  <Flexbox
                    flexDirection="column"
                    position="relative"
                    style={{ height: '100%' }}
                  >
                    <Box mb={1}>
                      <SearchFilters change={change} />
                    </Box>
                    <Desktop>
                      <StickyContainer>
                        <Box
                          display="flex"
                          justifyContent="center"
                          flexWrap="wrap"
                          mt={1}
                        >
                          <AdsSlot
                            slotId="gam_mrec_posL1"
                            sizes={[[300, 250]]}
                            shouldRefresh={() => false}
                            sizeMapping={[
                              { viewport: [895, 0], sizes: [[300, 250]] },
                              { viewport: [769, 0], sizes: [] },
                              {
                                viewport: [0, 0],
                                sizes: [
                                  [300, 250],
                                  [320, 100],
                                  [320, 50],
                                  [300, 50],
                                  'fluid'
                                ]
                              }
                            ]}
                            targetingArguments={{ pos: 'L1' }}
                          />
                        </Box>
                      </StickyContainer>
                    </Desktop>
                  </Flexbox>
                </Grid>
                <Grid item xs={12} md={8} lg={6}>
                  <Box display="flex" flexDirection="column">
                    <SearchToolBar
                      orientation={orientation}
                      changeOrientation={setOrientation}
                    />
                    <Box flex={1}>
                      <Grid container spacing={3}>
                        <LoaderBox length={currentListings?.length}>
                          <SearchCatDescription
                            catDescription={chosenCatDesc}
                            subCatDescription={chosenSubCatDesc}
                          />
                          <SearchProductList
                            orientation={orientation}
                            listings={currentListings}
                            loading={isFetching}
                          />
                        </LoaderBox>
                      </Grid>
                    </Box>
                    {pagy?.pages > 1 && (
                      <Box
                        mx="auto"
                        display="flex"
                        justifyContent="center"
                        mt={4}
                      >
                        <Pagination
                          count={pagy?.pages}
                          color="primary"
                          shape="rounded"
                          size={isMobile ? 'small' : 'large'}
                          onChange={handleChangePage}
                          page={+router.query.page || 1}
                        />
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} lg={3}>
                  <SearchSidebar />
                </Grid>
              </Grid>
            </Box>
          </Container>
          <Box display="flex" justifyContent="center" flexWrap="wrap" my={2}>
            <AdsSlot
              slotId="gam_footer_pos3"
              targetingArguments={{ pos: '3' }}
              sizes={[[300, 250]]}
              shouldRefresh={() => false}
              sizeMapping={[
                {
                  viewport: [1280, 0],
                  sizes: [
                    [970, 90],
                    [728, 90]
                  ]
                },
                { viewport: [769, 0], sizes: [[728, 90]] },
                {
                  viewport: [0, 0],
                  sizes: [
                    [300, 250],
                    [320, 100],
                    [320, 50],
                    [300, 50]
                  ]
                }
              ]}
            />
          </Box>
        </AppLayout>
      </DFPSlotsProvider>
    </>
  )
}

const mapStateToProps = (state, { router }) => {
  const initialValues = Paths.search.parseSearch(router.asPath)

  return {
    initialValues
  }
}

const withConnect = connect(mapStateToProps)

let sortingTriggired = null

const withForm = reduxForm<
  SearchFormValues,
  SingletonRouter & { isMobile: boolean }
>({
  form: 'Listings_Filters_Form',
  destroyOnUnmount: true,
  shouldAsyncValidate: () => false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: false,
  onChange: (values, dispatch, { submit, change, isMobile }, prevValues) => {
    // remove dependent values
    if (
      values.by_type !== prevValues.by_type &&
      values.category === prevValues.category &&
      !!values.category
    ) {
      dispatch(change('category', null))
      return
    }

    if (values.make?.name.length < 1) {
      dispatch(change('make', null))
      return
    }

    if (values.page && values.page === prevValues.page) {
      sortingTriggired = values.sorting !== prevValues.sorting
      dispatch(change('page', null))
      return
    }

    const hasMobileSubmit = () => {
      const excludedCriteria = ['sorting', 'page']
      const changedValues = diff(prevValues, values) as any
      const wasDeleted = Object.entries(changedValues)
        .filter(([key]) => !excludedCriteria.includes(key))
        .some(([_key, value]) => value === null)

      const isPageChanged = 'page' in changedValues

      const condition =
        wasDeleted ||
        'sorting' in changedValues ||
        (isOfType.boolean(sortingTriggired)
          ? isPageChanged && sortingTriggired
          : isPageChanged)

      sortingTriggired = null

      return condition
    }

    const isMobileSubmit = hasMobileSubmit()
    const isSubmit = !isMobile || isMobileSubmit

    if (isSubmit) {
      sortingTriggired = null
      submit()
    }
  },
  onSubmit: (values, _dispatch, { router, isMobile }) => {
    const isExistSubCategory = typeof values.category !== 'string'
    const needRemoveSubCategory = isMobile && !isExistSubCategory

    const currentValues = {
      ...values,
      ...(needRemoveSubCategory && {
        category: null
      })
    }

    const pathname = Paths.search.buildSearch(currentValues)

    if (pathname !== router.asPath) {
      router.push(pathname, undefined, {
        shallow: true
      })
    }
  }
})

export default compose<any>(
  withRouter,
  withConnect,
  withMobile,
  withForm
)(SearchView)
